import {
  useConsumerAnalytics,
  useConsumerMaturityDistribution,
  useConsumerExtensionStats,
} from '@/hooks/useAdminConsumers'

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-5">
      <div className="text-[#8b949e] font-mono text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className="text-[#FFB84D] font-mono text-2xl font-bold">{value}</div>
      {sub && <div className="text-[#484f58] font-mono text-xs mt-1">{sub}</div>}
    </div>
  )
}

function MaturityBar({ label, count, total }: { label: string; count: number; total: number }) {
  const pct = total > 0 ? (count / total) * 100 : 0
  return (
    <div className="flex items-center gap-3 mb-2">
      <div className="w-20 text-[#8b949e] font-mono text-xs uppercase">{label}</div>
      <div className="flex-1 bg-[#21262d] rounded-full h-4 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#FFB84D]/60 to-[#FFB84D] rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="w-16 text-right text-[#c9d1d9] font-mono text-xs">{count} ({pct.toFixed(0)}%)</div>
    </div>
  )
}

export default function ConsumerAnalytics() {
  const { data: analytics, isLoading: loadingAnalytics } = useConsumerAnalytics(30)
  const { data: maturity, isLoading: loadingMaturity } = useConsumerMaturityDistribution()
  const { data: extension, isLoading: loadingExtension } = useConsumerExtensionStats(30)

  if (loadingAnalytics || loadingMaturity || loadingExtension) {
    return (
      <div className="text-center py-12 text-[#FFB84D] font-mono">Loading analytics...</div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Overview Stats */}
      {analytics && (
        <div>
          <h2 className="text-[#FFB84D] font-mono text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-[#FFB84D] rounded-full animate-pulse" />
            Overview (Last 30 Days)
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Consumers" value={analytics.total_consumers} />
            <StatCard label="Active" value={analytics.active_consumers} />
            <StatCard label="New (30d)" value={analytics.new_registrations} />
            <StatCard
              label="Onboarding Rate"
              value={`${analytics.onboarding_completion_rate.toFixed(1)}%`}
            />
          </div>
        </div>
      )}

      {/* Tier Distribution */}
      {analytics && (
        <div>
          <h2 className="text-[#FFB84D] font-mono text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-[#FFB84D] rounded-full" />
            Tier Distribution
          </h2>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(analytics.tier_distribution).map(([tier, count]) => (
              <StatCard
                key={tier}
                label={tier}
                value={count}
                sub={`${analytics.total_consumers > 0 ? ((count / analytics.total_consumers) * 100).toFixed(1) : 0}%`}
              />
            ))}
            {Object.keys(analytics.tier_distribution).length === 0 && (
              <div className="col-span-3 text-center py-6 text-[#484f58] font-mono text-sm">
                No consumers yet
              </div>
            )}
          </div>
        </div>
      )}

      {/* Profile Maturity */}
      {maturity && (
        <div>
          <h2 className="text-[#FFB84D] font-mono text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-[#FFB84D] rounded-full" />
            Profile Maturity ({maturity.total_profiles} profiles)
          </h2>
          <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-5">
            <MaturityBar label="None" count={maturity.maturity_distribution.none || 0} total={maturity.total_profiles} />
            <MaturityBar label="Low" count={maturity.maturity_distribution.low || 0} total={maturity.total_profiles} />
            <MaturityBar label="Medium" count={maturity.maturity_distribution.medium || 0} total={maturity.total_profiles} />
            <MaturityBar label="High" count={maturity.maturity_distribution.high || 0} total={maturity.total_profiles} />
            <MaturityBar label="Mature" count={maturity.maturity_distribution.mature || 0} total={maturity.total_profiles} />
          </div>
        </div>
      )}

      {/* Extension Usage */}
      {extension && (
        <div>
          <h2 className="text-[#FFB84D] font-mono text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-[#FFB84D] rounded-full" />
            Extension Usage (Last 30 Days)
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Sessions" value={extension.total_sessions} />
            <StatCard label="Utterances" value={extension.total_utterances} />
            <StatCard label="Distillations" value={extension.total_distillations} />
            <StatCard label="Insights" value={extension.total_insights} />
            <StatCard label="Active Users" value={extension.active_users} />
          </div>
        </div>
      )}
    </div>
  )
}
